<script setup lang="ts">
import { debounce } from 'lodash';
import { onMounted, ref, computed, watch } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { Widget, DashboardWidget } from '@/interfaces/Dashboard';
import ConnxStatusRowCard from '@/components/Atoms/ConnxStatusRowCard.vue';
import AlertType from '@/interfaces/AlertType';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';

const props = defineProps<{ height: number; widget: DashboardWidget }>();

const dashboardStore = useDashboardStore();

interface ApiSeries {
  name: string;
  data: { label: string; value: number };
}

const apiSeries = ref<ApiSeries[]>([]);

const benchmarks = ref<[number, number] | null>(null);

const enrichedSeries = computed(() => {
  const [hi, lo] = benchmarks.value ?? [0, 0];

  return apiSeries.value.map((item) => {
    const val = Number(item.data?.value ?? 0);

    const type: AlertType = val >= hi ? 'success' : val < hi && val >= lo ? 'warning' : 'error';

    return {
      name: item.name,
      value: val,
      label: item.data?.label ?? '',
      type,
    };
  });
});

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const fetchData = async () => {
  const res = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
    widgetGuid: props.widget.dashboardTabWidgetGuid,
  });
  const seriesFromApi: ApiSeries[] = res?.data?.series ?? [];
  apiSeries.value = seriesFromApi;

  if (Array.isArray(res?.benchmarks) && res.benchmarks.length >= 2) {
    benchmarks.value = [Number(res.benchmarks[0]), Number(res.benchmarks[1])];
  }
};

onMounted(async () => {
  await fetchData();
});

watch(
  () => [props.widget?.defaultJsonProperties?.parameters],
  async () => {
    await debouncedFetchData();
  },
  { deep: true }
);
</script>

<template>
  <div class="tw-flex tw-items-center tw-justify-center tw-w-full" v-if="enrichedSeries.length">
    <div
      class="ScrollBar ScrollBar--vertical tw-w-full tw-flex"
      :style="{ maxHeight: `${height}px`, overflowY: 'auto' }"
    >
      <!-- <div
        :style="{ height: `${height}px` }"
        class="tw-py-4 tw-flex tw-justify-center tw-items-center"
      > -->
      <div class="tw-w-full tw-py-4">
        <div class="tw-grid tw-gap-y-2 tw-border tw-border-gray-300 tw-px-1 tw-items-center">
          <ConnxStatusRowCard
            :status="item.type"
            v-for="(item, index) in enrichedSeries"
            :key="index"
            :title="item.name"
            :value="item.label"
          ></ConnxStatusRowCard>
        </div>
      </div>
      <!-- </div> -->
    </div>
  </div>
  <connx-dashboard-widget-no-data v-else />
</template>
